const isNumericEnumReverseMappingKey = (
  enumObject: object,
  key: string
): boolean => {
  const mapped = (enumObject as Record<string, unknown>)[key];
  return (
    typeof mapped === "string" &&
    Object.hasOwn(enumObject, mapped) &&
    Object.is((enumObject as Record<string, unknown>)[mapped], Number(key))
  );
};

/**
 * @returns An array containing all keys from the given enum object,
 * excluding numeric-enum reverse mappings. 
 * (`{"A": 0, "0": "A"}` -> `["A"]`)
 * @remarks
 * This should not be called with anything but a TypeScript `enum`
 * (either numeric or string).
 */
// TODO: Implement a type guard that only allows numeric enums
// (existing callsites can use Object.keys or one of the other helpers)
export const enumKeys = <T extends object>(
  enumObject: T
): Array<keyof T> =>
  Object.keys(enumObject).filter(
    (key) => !isNumericEnumReverseMappingKey(enumObject, key)
  ) as Array<keyof T>;

/**
 * @returns An array containing all values from the given enum object,
 * excluding numeric-enum reverse mappings. 
 * (`{"A": 0, "0": "A"}` -> `[0]`)
 * @remarks
 * This should not be called with anything but a TypeScript `enum`
 * (either numeric or string).
 */
export const enumValues = <T extends object>(enumObject: T): T[keyof T][] =>
  enumKeys(enumObject).map((key) => enumObject[key]);

// TODO: Rename/remove this - it is emphatically not enum-related and is just Array.includes
// but wider
export const isInEnum = <T>(value: unknown, values: T[]): value is T =>
  values.includes(value as T);

export function isEnumValue<T extends object>(
  enumObject: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
): value is T[keyof T];
// TODO: Delete this overload - it is just a string overload of isInEnum
export function isEnumValue<T extends string>(
  enumObject: Array<T>,
  value: string
): value is T;
export function isEnumValue<T>(
  enumObject: T[] | object,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
): value is T {
  return Array.isArray(enumObject)
    ? enumObject.includes(value)
    : enumValues(enumObject as unknown as object & T).includes(value);
}

export const isEnumKey = <T extends object>(
  enumObject: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
): value is keyof T => enumKeys(enumObject).includes(value);

/**
 * Gets the enum value if the given key is in the enum, or return undefined.
 */
export const enumValue = <T extends object>(
  enumObject: T,
  key: string | undefined | null
): T[keyof T] | undefined => {
  return isEnumKey(enumObject, key) ? enumObject[key] : undefined;
};
